import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BoltIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { usePayment } from '../context/PaymentContext';
import { isGuestPath, isOnboardingPath } from '../utils/onboardingLanding';
import { PLAN_LINE, PLAN_LINE_CTA } from '../content/freeRead';
import { track, EVENTS } from '../services/analytics';

const DISMISS_KEY = 'lowCreditsBannerDismissed';

/**
 * The free plan's wall (#444, narrowed 2026-08-01, reworded #4668): shows
 * ONLY when a FREE account's ledger is at zero, and only then.
 *
 * It originally warned at "3 or fewer", but a pre-emptive "Only 2 left"
 * while the student still has room is exactly the rationing cue the
 * no-credit-talk rule bans: the sanctioned moment for the offer is the wall
 * itself, not the approach to it. Since #4668 the words never name the
 * currency or a count: the banner says what the Season Pass covers.
 *
 * A Pass holder never sees it (founder 2026-10-18: the Pass "covers
 * everything without any mention of any usage or credits"); if the ledger
 * refuses one of their actions the server's 402 opens the modal's fair-use
 * note instead. The CTA routes to the real /pricing page, not a duplicate
 * modal. Dismissible for the session.
 */
const LowCreditsBanner = () => {
    const { credits, creditsRemaining, creditsLoading, isFreeTier, trialEnded, freeRead } = usePayment();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
    });

    // Only for a signed-in FREE user once their balance has loaded (never a
    // flash before the fetch, never on public pages while signed out, never
    // for a Pass holder), and only once they are actually AT the wall.
    // Never over a first-run journey (/welcome, /start): the list read there
    // carries its own plan line per school (#4702). Never on /pricing, the
    // page it points to: there it repeated the page and ran under the fixed
    // marketing header on a phone (#4705 mock round).
    if (creditsLoading || !credits || dismissed || !isFreeTier || creditsRemaining > 0
        || isOnboardingPath(pathname) || isGuestPath(pathname) || pathname === '/pricing') {
        return null;
    }

    const dismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div className="bg-[#FCEEE8] border-b border-[#F0D8CC] px-4 py-2.5 flex items-center justify-center gap-3 text-sm">
            <BoltIcon className="w-4 h-4 text-[#C05838] flex-shrink-0" />
            <span className="text-[#7A3A22]" data-testid="plan-line">{freeRead?.spent
                // One free read, then the price (#5631, mock A): what the
                // student got, then what the Pass covers.
                ? PLAN_LINE
                : trialEnded
                // After the 7-day trial (#4703): what they keep, never what they lost.
                ? 'Your 7 days of everything are over, and everything you made is still here. The Season Pass keeps it going, all season.'
                : <>That&rsquo;s everything the Free plan includes. The Season Pass covers everything, all season.</>}</span>
            <button
                onClick={() => {
                    if (freeRead?.spent) track(EVENTS.UPGRADE_CTA_CLICKED, { surface: 'plan_line' });
                    navigate('/pricing');
                }}
                className="font-semibold text-[#C05838] hover:underline whitespace-nowrap"
            >
                {PLAN_LINE_CTA} →
            </button>
            <button onClick={dismiss} aria-label="Dismiss" className="ml-1 p-1 hover:bg-white/60 rounded">
                <XMarkIcon className="w-4 h-4 text-[#7A3A22]" />
            </button>
        </div>
    );
};

export default LowCreditsBanner;
